import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateGiftCards } from './gift-cards.api';

/**
 * Mint a batch of blank codes.
 *
 * The codes arrive as a text file and are downloaded once — the API keeps no
 * "show me that batch again" endpoint, and the cards are indistinguishable in
 * the list afterwards. That makes the download the only copy, so the dialog
 * says so before it runs rather than after.
 *
 * The batch is capped here. The service writes each card in its own
 * `SaveChangesAsync` inside the loop, with a uniqueness query per card, so a
 * large batch is a long transaction storm against the gift card table; the
 * original offered an unbounded number box.
 */

/** Above this the request reliably outlives the 120s client timeout. */
const MAX_BATCH = 500;

export function CreateCardsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('10');
  const create = useCreateGiftCards();

  useEffect(() => {
    if (open) setAmount('10');
  }, [open]);

  const parsed = Number(amount);
  const valid =
    Number.isInteger(parsed) && parsed > 0 && parsed <= MAX_BATCH;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('giftCards.createCards')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('giftCards.createBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="batch-size" className="text-xs font-semibold">
            {t('giftCards.howMany')}
          </Label>
          <Input
            id="batch-size"
            type="number"
            min={1}
            max={MAX_BATCH}
            value={amount}
            disabled={create.isPending}
            onChange={(event) => setAmount(event.target.value)}
          />
          <p className="text-muted-foreground text-xs">
            {t('giftCards.batchLimit', { max: MAX_BATCH })}
          </p>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={create.isPending}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={!valid || create.isPending}
            onClick={(event) => {
              // The dialog closes on its own otherwise, and the download would
              // land after it had gone.
              event.preventDefault();
              create.mutate(parsed, {
                onSuccess: (codes) => {
                  toast.success(
                    t('giftCards.created', { count: codes.length })
                  );
                  onOpenChange(false);
                },
                onError: (error) =>
                  toast.error(
                    error instanceof Error
                      ? error.message
                      : t('giftCards.actionFailed')
                  ),
              });
            }}
          >
            {create.isPending && <Loader2 className="animate-spin" />}
            {t('giftCards.create')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
